import { DatalayerService } from '@analytics/datalayer';
import { AnalyticsActionTypes } from '@analytics/models';
import { AnalyticsUtils, ToolTrackingUtils } from '@analytics/utils';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import type { TypedComponentRendering } from '@core/jss-models';
import { Status } from '@essent/common';
import type { InstallationAddress } from '@essent/installation-address';
import type { FullCalculateFormValues } from '@innogy/become-a-customer/shared';
import {
  type CalculateComponentContent,
  type CalculateComponentRendering,
} from '@innogy/become-a-customer/shared/interfaces';
import {
  getPromoCodeFormState,
  getPromoCodeId,
} from '@innogy/become-a-customer/state';
import type { DropdownItem } from '@innogy/common-ui/forms';
import { Store } from '@ngrx/store';
import { FormGroupState } from 'ngrx-forms';

@Component({
  selector: 'wl-calculate-form',
  templateUrl: './calculate-form.component.html',
  styleUrls: [
    './calculate-form.component.ed.scss',
    './calculate-form.component.essent.scss',
  ],
})
export class CalculateFormComponent {
  private readonly toolTrackingUtils = inject(ToolTrackingUtils);
  private readonly analyticsUtils = inject(AnalyticsUtils);
  private readonly dataLayerService = inject(DatalayerService);

  constructor(private readonly store$: Store) {}

  readonly rendering =
    input<TypedComponentRendering<CalculateComponentRendering>>();
  readonly content = input.required<CalculateComponentContent>();
  readonly formState =
    input.required<FormGroupState<FullCalculateFormValues>>();
  readonly installationAddressStatus = input<Status>();
  readonly installationAddresses = input<InstallationAddress[]>();
  readonly houseNumberExtensions = input<DropdownItem[]>();
  readonly showManualHouseNumberAdditionInput = input<boolean>();
  readonly submitButtonDisabled = input<boolean>();
  readonly showSpinnerWhenSubmitButtonDisabled = input<boolean>();
  readonly isInModal = input<boolean>(false);
  readonly installationAddressNotFound = input<boolean>();

  readonly promoCode = this.store$.selectSignal(getPromoCodeId);
  readonly promoCodeFormState = this.store$.selectSignal(getPromoCodeFormState);

  submitCalculate = output();
  openModal = output<boolean>();
  hideHouseNumberInput = output();
  submitAlternateAddress = output();

  readonly hideTriggerContainer = signal(false);
  readonly addressNextButtonClicked = signal(false);

  readonly controls = computed(() => this.formState().controls);

  readonly isAddressManualEntryEnabled = computed(
    () => this.content().enableAlternateAddressEntry
  );
  readonly isUsageQuestionnaireTriggerEnabled = computed(
    () => this.content()?.enableUsageQuestionnaireTrigger
  );
  readonly showPromoCodeForm = computed(
    () =>
      this.content().enablePromoCodeInput &&
      !(
        this.promoCodeFormState().isSubmitted &&
        this.promoCodeFormState().isValid
      )
  );

  /**
   * When the alternate address feature is enabled, this check will pass when the user has completed that entry
   * Otherwise it is always true
   */
  readonly isAddressManualEntryCompleted = computed(() =>
    this.isAddressManualEntryEnabled() ? this.addressNextButtonClicked() : true
  );

  /**
   * When the usage questionnaire trigger feature is enabled, this check will pass when the user has completed that step
   * Otherwise it is always true
   */
  readonly isUsageQuestionnaireTriggerCompleted = computed(() =>
    this.isUsageQuestionnaireTriggerEnabled()
      ? this.hideTriggerContainer()
      : true
  );

  /**
   * Checks if the usage form has been (partially) completed
   */
  readonly usageQuestionnaireFormCompleted = computed(() => {
    if (this.controls().doubleMeter?.value) {
      return (
        this.controls().electricityUsageNormal?.isValid ||
        this.controls().electricityUsageOffPeak?.isValid
      );
    } else {
      return this.controls().electricityUsage?.isValid;
    }
  });

  /**
   * Determines if the usage questionnaire trigger should be shown, only if:
   * - The feature is enabled
   * - It is not already dismissed
   * - The usage form has not been (partially) completed yet
   * - The manual address entry feature is enabled and completed
   */
  readonly showUsageQuestionnaireTrigger = computed(() => {
    return !(
      !this.isUsageQuestionnaireTriggerEnabled() ||
      this.hideTriggerContainer() ||
      this.usageQuestionnaireFormCompleted() ||
      !this.isAddressManualEntryCompleted()
    );
  });

  /**
   * Determines if the usage form should be shown:
   * - The usage form has been (partially) completed (also happens with the questionnaire modal)
   * - The usage trigger feature is enabled and has been dismissed
   * - The manual address feature has been enabled and it is completed
   */
  readonly showCalculateFormContainer = computed(() => {
    return (
      this.usageQuestionnaireFormCompleted() ||
      (this.isUsageQuestionnaireTriggerCompleted() &&
        this.isAddressManualEntryCompleted())
    );
  });

  setAddressNextButtonClicked() {
    this.addressNextButtonClicked.set(
      !(
        this.controls()?.postalCode?.isPristine ||
        this.controls()?.postalCode?.isInvalid ||
        this.controls()?.houseNumber?.isPristine ||
        this.controls()?.houseNumber?.isInvalid
      )
    );
    this.submitAlternateAddress.emit();
  }

  async trackPromoCodeSubmit() {
    if (!this.promoCodeFormState().isValid) {
      this.dataLayerService.pushEvent(
        this.toolTrackingUtils.createToolEvent(
          AnalyticsActionTypes.LAST_STEP,
          {
            result: {
              promoCode: this.promoCodeFormState().value.promoCode,
            },
          },
          {
            name: 'calculate',
            stepname: 'promo code',
            placement: await this.analyticsUtils.getElementPlacement(
              this.rendering()?.componentName
            ),
            step: 0,
          }
        )
      );
    }
  }
}
